// A2F — preserve the user's place across a full-page OAuth round-trip.
//
// reconnectGraph() (lib/AuthContext.tsx) does a full-page Supabase OAuth
// redirect to refresh the Microsoft Graph token. That unmounts the SPA, and the
// callback (modules/auth/OAuthCallbackPage.tsx) otherwise lands everyone on '/'.
// We stash the route the user reconnected from here so the callback can send
// them back instead of dumping them on the dashboard.
//
// sessionStorage (not localStorage) is the right carrier: it is per-origin,
// per-tab, and survives the top-level navigation out to login.microsoftonline.com
// and back within the same tab. Storage access is wrapped in try/catch and
// failures are swallowed (private mode / quota), mirroring lib/ActiveOrgContext.

const KEY = 'chrona_post_auth_redirect';

/**
 * A same-origin in-app route is one with a single leading slash that is neither a
 * protocol-relative ("//host") nor a backslash-normalized authority ("/\host",
 * which browsers can fold to "//host") and not an /auth/* route (which would
 * bounce the user back through login). This is the open-redirect guard. The
 * "/\\" check is load-bearing, not redundant with "//".
 *
 * Safe because the consumed value is only ever handed to react-router's
 * navigate() (same-origin resolution), never to window.location / an anchor href.
 */
function isInternalPath(path: unknown): path is string {
  if (typeof path !== 'string' || !path.startsWith('/')) return false;
  if (path.startsWith('//') || path.startsWith('/\\')) return false;
  if (path.startsWith('/auth/')) return false;
  return true;
}

/** Remember where to return after the OAuth round-trip. No-op for non-internal paths. */
export function stashPostAuthRedirect(path: string): void {
  try {
    if (isInternalPath(path)) sessionStorage.setItem(KEY, path);
  } catch {
    /* storage unavailable — return path just falls back to '/' */
  }
}

/**
 * Read and clear the stashed return path (single-use). Returns null when nothing
 * was stashed or the stored value fails validation, so the callback can default
 * to '/'.
 */
export function consumePostAuthRedirect(): string | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    return isInternalPath(raw) ? raw : null;
  } catch {
    return null;
  }
}
